/**
 * GitHub API Service
 * For deploying generated sites to GitHub Pages
 * Supports OAuth Device Flow for better UX
 */

const GITHUB_API = 'https://api.github.com'

// OAuth App Client ID (public - no secret needed for Device Flow)
// Users can set their own via localStorage for self-hosted instances
const DEFAULT_CLIENT_ID = 'Ov23liVl8FwfXQ8JXlhD' // EMD Factory public app
const getClientId = () => localStorage.getItem('emd_github_client_id') || DEFAULT_CLIENT_ID

/**
 * Get stored GitHub token
 */
export function getToken() {
  const keys = JSON.parse(localStorage.getItem('emd_api_keys') || '{}')
  return keys.github || null
}

/**
 * Store GitHub token
 */
export function setToken(token) {
  const keys = JSON.parse(localStorage.getItem('emd_api_keys') || '{}')
  keys.github = token
  localStorage.setItem('emd_api_keys', JSON.stringify(keys))
}

/**
 * OAuth Device Flow - Step 1: Request device code
 * Note: Requires a proxy due to CORS. Falls back to PAT flow if unavailable.
 */
export async function requestDeviceCode() {
  const proxyUrl = localStorage.getItem('emd_oauth_proxy') || '/api/github/device-code'

  try {
    const response = await fetch(proxyUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client_id: getClientId(),
        scope: 'repo workflow'
      })
    })

    if (!response.ok) {
      throw new Error('Device flow not available')
    }

    return response.json()
  } catch (error) {
    // Device flow not available, will fall back to PAT
    return null
  }
}

/**
 * OAuth Device Flow - Step 2: Poll for access token
 */
export async function pollForToken(deviceCode, interval = 5) {
  const proxyUrl = localStorage.getItem('emd_oauth_proxy_token') || '/api/github/access-token'

  return new Promise((resolve, reject) => {
    const poll = async () => {
      try {
        const response = await fetch(proxyUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            client_id: getClientId(),
            device_code: deviceCode,
            grant_type: 'urn:ietf:params:oauth:grant-type:device_code'
          })
        })

        const data = await response.json()

        if (data.access_token) {
          resolve(data.access_token)
        } else if (data.error === 'authorization_pending') {
          setTimeout(poll, interval * 1000)
        } else if (data.error === 'slow_down') {
          setTimeout(poll, (interval + 5) * 1000)
        } else if (data.error === 'expired_token') {
          reject(new Error('Code expired'))
        } else if (data.error === 'access_denied') {
          reject(new Error('Access denied'))
        } else {
          setTimeout(poll, interval * 1000)
        }
      } catch (error) {
        reject(error)
      }
    }

    poll()
  })
}

/**
 * Check if OAuth Device Flow is available
 */
export async function isDeviceFlowAvailable() {
  try {
    const response = await fetch('/api/github/health', { method: 'HEAD' })
    return response.ok
  } catch {
    return false
  }
}

/**
 * Check if GitHub is configured
 */
export function isConfigured() {
  return !!getToken()
}

/**
 * Get authenticated user info
 */
export async function getUser() {
  const token = getToken()
  if (!token) throw new Error('GitHub token not configured')

  const response = await fetch(`${GITHUB_API}/user`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/vnd.github.v3+json',
    },
  })

  if (!response.ok) {
    throw new Error('Invalid GitHub token')
  }

  return response.json()
}

/**
 * Check if a repository exists
 */
export async function repoExists(owner, repo) {
  const token = getToken()
  if (!token) throw new Error('GitHub token not configured')

  const response = await fetch(`${GITHUB_API}/repos/${owner}/${repo}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/vnd.github.v3+json',
    },
  })

  return response.ok
}

/**
 * Create a new repository
 */
export async function createRepo(name, options = {}) {
  const token = getToken()
  if (!token) throw new Error('GitHub token not configured')

  const { description = '', isPrivate = false, autoInit = false } = options

  const response = await fetch(`${GITHUB_API}/user/repos`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/vnd.github.v3+json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      name,
      description,
      private: isPrivate,
      auto_init: autoInit,
      has_pages: true,
    }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to create repository')
  }

  return response.json()
}

/**
 * Get or create a file in the repository
 */
async function getFileSha(owner, repo, path) {
  const token = getToken()

  const response = await fetch(`${GITHUB_API}/repos/${owner}/${repo}/contents/${path}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/vnd.github.v3+json',
    },
  })

  if (response.ok) {
    const data = await response.json()
    return data.sha
  }

  return null
}

/**
 * Upload a single file to the repository
 */
export async function uploadFile(owner, repo, path, content, message) {
  const token = getToken()
  if (!token) throw new Error('GitHub token not configured')

  // Check if file exists to get SHA for update
  const sha = await getFileSha(owner, repo, path)

  const body = {
    message,
    content: btoa(unescape(encodeURIComponent(content))), // Base64 encode
  }

  if (sha) {
    body.sha = sha
  }

  const response = await fetch(`${GITHUB_API}/repos/${owner}/${repo}/contents/${path}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/vnd.github.v3+json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || `Failed to upload ${path}`)
  }

  return response.json()
}

/**
 * Upload a binary file (images) to the repository
 */
export async function uploadBinaryFile(owner, repo, path, blob, message) {
  const token = getToken()
  if (!token) throw new Error('GitHub token not configured')

  // Convert blob to base64
  const buffer = await blob.arrayBuffer()
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  const base64 = btoa(binary)

  // Check if file exists
  const sha = await getFileSha(owner, repo, path)

  const body = {
    message,
    content: base64,
  }

  if (sha) {
    body.sha = sha
  }

  const response = await fetch(`${GITHUB_API}/repos/${owner}/${repo}/contents/${path}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/vnd.github.v3+json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || `Failed to upload ${path}`)
  }

  return response.json()
}

/**
 * Enable GitHub Pages for a repository
 */
export async function enablePages(owner, repo, branch = 'main') {
  const token = getToken()
  if (!token) throw new Error('GitHub token not configured')

  const response = await fetch(`${GITHUB_API}/repos/${owner}/${repo}/pages`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/vnd.github.v3+json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      source: {
        branch,
        path: '/',
      },
    }),
  })

  // 409 means Pages already enabled
  if (!response.ok && response.status !== 409) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to enable GitHub Pages')
  }

  return true
}

/**
 * Get GitHub Pages URL for a repository
 */
export async function getPagesUrl(owner, repo) {
  const token = getToken()
  if (!token) throw new Error('GitHub token not configured')

  const response = await fetch(`${GITHUB_API}/repos/${owner}/${repo}/pages`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/vnd.github.v3+json',
    },
  })

  if (response.ok) {
    const data = await response.json()
    return data.html_url
  }

  // Default URL if Pages not yet configured
  return `https://${owner}.github.io/${repo}`
}

/**
 * Deploy a complete site to GitHub Pages
 * @param {Object} options
 * @param {string} options.repoName - Repository name
 * @param {Object} options.files - { filename: content } for HTML files
 * @param {Object} options.images - { filename: blob } for images
 * @param {Function} options.onProgress - Progress callback
 */
export async function deploySite({ repoName, files, images = {}, onProgress }) {
  const user = await getUser()
  const owner = user.login

  onProgress?.({ step: 'init', message: 'Vérification du repository...' })

  // Create repo if doesn't exist
  const exists = await repoExists(owner, repoName)
  if (!exists) {
    onProgress?.({ step: 'create', message: 'Création du repository...' })
    await createRepo(repoName, {
      description: `Site généré par EMD Factory`,
      isPrivate: false,
      autoInit: true,
    })
    // Wait a bit for repo to be ready
    await new Promise(resolve => setTimeout(resolve, 2000))
  }

  // Upload HTML files
  const totalFiles = Object.keys(files).length + Object.keys(images).length
  let uploadedCount = 0

  for (const [filename, content] of Object.entries(files)) {
    onProgress?.({
      step: 'upload',
      message: `Upload ${filename}...`,
      progress: uploadedCount / totalFiles,
    })
    await uploadFile(owner, repoName, filename, content, `Add ${filename}`)
    uploadedCount++
  }

  // Upload images
  for (const [filename, blob] of Object.entries(images)) {
    onProgress?.({
      step: 'upload',
      message: `Upload ${filename}...`,
      progress: uploadedCount / totalFiles,
    })
    await uploadBinaryFile(owner, repoName, `img/${filename}`, blob, `Add ${filename}`)
    uploadedCount++
  }

  // Enable GitHub Pages
  onProgress?.({ step: 'pages', message: 'Activation GitHub Pages...' })
  await enablePages(owner, repoName)

  // Get final URL
  const url = await getPagesUrl(owner, repoName)

  onProgress?.({ step: 'done', message: 'Déploiement terminé!', url })

  return {
    success: true,
    url,
    repo: `https://github.com/${owner}/${repoName}`,
  }
}

export default {
  getToken,
  setToken,
  isConfigured,
  getUser,
  createRepo,
  uploadFile,
  uploadBinaryFile,
  enablePages,
  getPagesUrl,
  deploySite,
  requestDeviceCode,
  pollForToken,
  isDeviceFlowAvailable,
}
